import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { useActivityLogs } from "@features/cx/paymentsv2/hooks";
import { TIMELINE_LIMIT } from "@shared/utils/constants";
import { Plus, Clock } from "lucide-react";

interface TimelineTabProps {
  remoteUtilityId: any;
  entityId?: string;
  requestId?: string;
  actionType?: string;
  limit?: number;
  objectType?: string;
  module?: string;
}

export function TimelineTab({
  remoteUtilityId,
  entityId,
  requestId,
  limit,
  objectType,
  module = "cx",
}: TimelineTabProps) {
  const reqId = requestId || entityId;
  const { data: activityLogs, isLoading } = useActivityLogs({
    remoteUtilityId: Number(remoteUtilityId),
    objectType,
    module,
    limit,
    requestId: reqId,
  });

  return (
    <Card>
      {limit !== TIMELINE_LIMIT && (
        <CardHeader>
          <CardTitle className="text-lg">Timeline</CardTitle>
        </CardHeader>
      )}
      <CardContent>
        {isLoading ? (
          <div className="flex h-40 items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : !activityLogs || activityLogs?.results?.length === 0 ? (
          <div className="text-center p-8">
            <div className="h-10 w-10 text-gray-400 mx-auto mb-2 flex items-center justify-center">
              <Clock className="h-8 w-8" />
            </div>
            <h3 className="text-lg font-medium text-gray-600">
              No Timeline Events
            </h3>
            <p className="text-sm text-gray-500">
              No timeline events available for this request
            </p>
          </div>
        ) : (
          <div>
            {activityLogs?.results?.map((event: any, index: number) => {
              const title =
                event.title ||
                event.event_title ||
                event.action_type ||
                event.objectType;
              ("Event");
              const changes =
                event.eventData?.changes ||
                event.event_data?.changes ||
                event.changes ||
                [];
              const eventActionType = event.action_type || event.actionType;
              const user =
                event.data?.lastModifiedUserRemoteName ||
                event.user_name ||
                event.user ||
                event.created_by ||
                "System User";
              const date =
                event.date ||
                event.created_on ||
                event.created_date ||
                event.timestamp ||
                "";

              return (
                <div key={index} className="mb-6 flex gap-3">
                  <div className="mt-1 flex-shrink-0 h-7 w-7 rounded-full bg-teal-50 border border-teal-200 flex items-center justify-center">
                    <Plus className="h-3.5 w-3.5 text-teal-600" />
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    {/* Title */}
                    <p className="font-semibold text-sm capitalize">{title}</p>

                    {/* Changes as badges */}
                    {(changes.length > 0 || eventActionType) && (
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        {eventActionType && (
                          <span className="inline-flex items-center text-xs px-2 py-0.5 rounded border border-blue-200 bg-blue-50 text-blue-700">
                            {eventActionType
                              .replace(/_/g, " ")
                              .replace(/\b\w/g, (c: string) => c.toUpperCase())}
                          </span>
                        )}
                        {changes.map((change: any, i: number) => {
                          const field = change.field || change.fieldName || "";
                          return (
                            <span
                              key={i}
                              className="inline-flex items-center text-xs px-2 py-0.5 rounded border border-blue-200 bg-blue-50 text-blue-700"
                            >
                              {field
                                .replace(/_/g, " ")
                                .replace(/\b\w/g, (c: string) =>
                                  c.toUpperCase(),
                                )}
                            </span>
                          );
                        })}
                      </div>
                    )}

                    {/* User and date */}
                    <p className="text-xs text-muted-foreground mt-1.5">
                      {user} · {date}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default TimelineTab;
